import React, { useState, useEffect } from 'react';
import { 
  FileCheck, Plus, Edit, Trash2, Download, Upload, 
  Search, Filter, Calendar, Building2, AlertCircle, 
  CheckCircle, Clock, RefreshCw, Eye, FileText, History, 
  CalendarPlus, Paperclip
} from 'lucide-react';
import Modal from './Modal';
import { useLanguage } from '../contexts/LanguageContext';

interface MaintenanceContract {
  id: number;
  contract_name: string;
  supplier_id: number;
  supplier_name: string;
  contract_start_date: string;
  contract_end_date: string;
  contract_file_path?: string;
  contract_filename?: string;
  description?: string;
  contract_price?: number;
  contract_currency?: 'TL' | 'EUR' | 'USD';
  status: 'active' | 'expired' | 'cancelled';
  created_by: number;
  created_by_name: string;
  updated_by?: number;
  updated_by_name?: string;
  created_at: string;
  updated_at: string;
}

interface Supplier {
  id: number;
  name: string;
}

const MaintenanceContractsPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [contracts, setContracts] = useState<MaintenanceContract[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({
    total: 0,
    expiring1Month: 0,
    expiring3Months: 0,
    expiring6Months: 0
  });
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showContractsModal, setShowContractsModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState<MaintenanceContract | null>(null);
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [uploadingContract, setUploadingContract] = useState(false);
  const [contractHistory, setContractHistory] = useState([]);
  const [contractFiles, setContractFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  const [extendData, setExtendData] = useState({
    extendType: 'days', // days, months, years, date
    extendValue: '',
    newEndDate: '',
    notes: ''
  });
  
  const [formData, setFormData] = useState({
    contract_name: '',
    supplier_id: '',
    contract_start_date: '',
    contract_end_date: '',
    description: '',
    contract_price: '',
    contract_currency: 'TL',
    status: 'active'
  });

  const statusLabels = {
    active: 'Aktif',
    expired: 'Süresi Dolmuş',
    cancelled: 'İptal Edilmiş'
  };

  const statusColors = {
    active: 'bg-green-100 text-green-800',
    expired: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
  };

  const statusIcons = {
    active: CheckCircle,
    expired: AlertCircle,
    cancelled: Clock
  };

  const currencyLabels = {
    TL: '₺',
    EUR: '€',
    USD: '$'
  };

  useEffect(() => {
    fetchContracts();
    fetchSuppliers();
  }, [currentPage, searchTerm, selectedStatus, selectedSupplier]);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(searchTerm && { search: searchTerm }),
        ...(selectedStatus && { status: selectedStatus }),
        ...(selectedSupplier && { supplier_id: selectedSupplier })
      });

      const response = await fetch(`/api/maintenance-contracts?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setContracts(data.data || []);
        setTotalPages(data.pagination?.pages || 1);
        calculateStats(data.data || []);
      }
    } catch (error) {
      console.error('Bakım anlaşmaları yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await fetch('/api/suppliers', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuppliers(data.data || []);
      }
    } catch (error) {
      console.error('Tedarikçiler yükleme hatası:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const url = selectedContract 
        ? `/api/maintenance-contracts/${selectedContract.id}`
        : '/api/maintenance-contracts';
      
      const method = selectedContract ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          ...formData,
          supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null
        })
      });

      if (response.ok) {
        setShowModal(false);
        setFormData({
          contract_name: '',
          supplier_id: '',
          contract_start_date: '',
          contract_end_date: '',
          description: '',
          contract_price: '',
          contract_currency: 'TL',
          status: 'active'
        });
        setSelectedContract(null);
        fetchContracts();
        
        // Sözleşme dosyası yüklenecekse
        if (contractFile && response.ok) {
          const contractData = await response.json();
          await uploadContractFile(contractFile, contractData.data.id);
        }
      } else {
        const errorData = await response.json();
        console.error('Hata:', errorData.error);
      }
    } catch (error) {
      console.error('Bakım anlaşması kaydetme hatası:', error);
    }
  };

  const uploadContractFile = async (file: File, contractId: number) => {
    try {
      setUploadingContract(true);
      
      const formData = new FormData();
      formData.append('contract', file);
      
      const response = await fetch(`/api/maintenance-contracts/${contractId}/contract`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (response.ok) {
        setContractFile(null);
        fetchContracts();
      } else {
        const errorData = await response.json();
        console.error('Sözleşme dosyası yükleme hatası:', errorData.error);
      }
    } catch (error) {
      console.error('Sözleşme dosyası yükleme hatası:', error);
    } finally {
      setUploadingContract(false);
    }
  };

  const handleEdit = (contract: MaintenanceContract) => {
    setSelectedContract(contract);
    setFormData({
      contract_name: contract.contract_name || '',
      supplier_id: contract.supplier_id ? contract.supplier_id.toString() : '',
      contract_start_date: contract.contract_start_date || '',
      contract_end_date: contract.contract_end_date || '',
      description: contract.description || '',
      contract_price: contract.contract_price ? contract.contract_price.toString() : '',
      contract_currency: contract.contract_currency || 'TL',
      status: contract.status || 'active'
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!selectedContract) return;
    
    try {
      const response = await fetch(`/api/maintenance-contracts/${selectedContract.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        setShowDeleteModal(false);
        setSelectedContract(null);
        fetchContracts();
      } else {
        const errorData = await response.json();
        console.error('Silme hatası:', errorData.error);
      }
    } catch (error) {
      console.error('Bakım anlaşması silme hatası:', error);
    }
  };

  const downloadContract = async (contract: MaintenanceContract) => {
    try {
      const response = await fetch(`/api/maintenance-contracts/${contract.id}/contract`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = contract.contract_filename || 'contract.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Dosya indirilemedi');
      }
    } catch (error) {
      console.error('Dosya indirme hatası:', error);
    }
  };

  const fetchContractHistory = async (contractId: number) => {
    try {
      const response = await fetch(`/api/maintenance-contracts/${contractId}/history`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContractHistory(data.data || []);
      }
    } catch (error) {
      console.error('Geçmiş yükleme hatası:', error);
    }
  };

  const fetchContractFiles = async (contractId: number) => {
    try {
      const response = await fetch(`/api/maintenance-contracts/${contractId}/files`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContractFiles(data.data || []);
      }
    } catch (error) {
      console.error('Dosyalar yükleme hatası:', error);
    }
  };

  const handleExtendContract = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContract) return;

    try {
      const response = await fetch(`/api/maintenance-contracts/${selectedContract.id}/extend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(extendData)
      });

      if (response.ok) {
        setShowExtendModal(false);
        setExtendData({ extendType: 'days', extendValue: '', newEndDate: '', notes: '' });
        fetchContracts();
        // Notification göster
        alert('Anlaşma süresi başarıyla uzatıldı!');
      }
    } catch (error) {
      console.error('Süre uzatma hatası:', error);
    }
  };

  const deleteContractFile = async (fileId: number, contractId: number) => {
    try {
      const response = await fetch(`/api/maintenance-contracts/files/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        fetchContractFiles(contractId);
        alert('Dosya başarıyla silindi!');
      } else {
        alert('Dosya silinemedi!');
      }
    } catch (error) {
      console.error('Dosya silme hatası:', error);
      alert('Dosya silme hatası: ' + error);
    }
  };

  const uploadNewContract = async (file: File, contractId: number) => {
    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      const formData = new FormData();
      formData.append('contract', file);
      
      const xhr = new XMLHttpRequest();
      
      return new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100;
            setUploadProgress(Math.round(percentComplete));
          }
        });
        
        xhr.addEventListener('load', () => {
          setIsUploading(false);
          setUploadProgress(0);
          
          if (xhr.status === 200) {
            fetchContractFiles(contractId);
            alert('Yeni sözleşme dosyası başarıyla yüklendi!');
            resolve(xhr.response);
          } else {
            alert('Dosya yükleme başarısız: ' + xhr.responseText);
            reject(new Error(xhr.responseText));
          }
        });
        
        xhr.addEventListener('error', () => {
          setIsUploading(false);
          setUploadProgress(0);
          alert('Dosya yükleme hatası oluştu');
          reject(new Error('Upload failed'));
        });
        
        xhr.open('POST', `/api/maintenance-contracts/${contractId}/files`);
        xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('auth_token')}`);
        xhr.send(formData);
      });
    } catch (error) {
      setIsUploading(false);
      setUploadProgress(0);
      console.error('Dosya yükleme hatası:', error);
      alert('Dosya yükleme hatası: ' + error);
    }
  };

  const getStatusIcon = (status: string) => {
    const IconComponent = statusIcons[status as keyof typeof statusIcons];
    return <IconComponent className="w-4 h-4" />;
  };

  const isExpiringSoon = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  const isExpired = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    return end < now;
  };

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const calculateStats = (contractsList: MaintenanceContract[]) => {
    const now = new Date();
    const oneMonth = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));
    const threeMonths = new Date(now.getTime() + (90 * 24 * 60 * 60 * 1000));
    const sixMonths = new Date(now.getTime() + (180 * 24 * 60 * 60 * 1000));

    const activeContracts = contractsList.filter(c => c.status === 'active');
    
    setStats({
      total: activeContracts.length,
      expiring1Month: activeContracts.filter(c => {
        const endDate = new Date(c.contract_end_date);
        return endDate <= oneMonth && endDate > now;
      }).length,
      expiring3Months: activeContracts.filter(c => {
        const endDate = new Date(c.contract_end_date);
        return endDate <= threeMonths && endDate > now;
      }).length,
      expiring6Months: activeContracts.filter(c => {
        const endDate = new Date(c.contract_end_date);
        return endDate <= sixMonths && endDate > now;
      }).length
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('maintenance.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base">
            {t('maintenance.subtitle')}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:flex sm:space-x-3 gap-2 w-full sm:w-auto">
          <button
            onClick={fetchContracts}
            className="flex items-center justify-center px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors min-h-[44px] text-sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Refresh' : 'Yenile'}
          </button>
          <button
            onClick={() => {
              setSelectedContract(null);
              setFormData({
                contract_name: '',
                supplier_id: '',
                contract_start_date: '',
                contract_end_date: '',
                description: '',
                contract_price: '',
                contract_currency: 'TL',
                status: 'active'
              });
              setShowModal(true);
            }}
            className="flex items-center justify-center px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors min-h-[44px] text-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('maintenance.newContract')}
          </button>
        </div>
      </div>

      {/* İstatistik Kartları */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 min-h-[80px] sm:min-h-[100px]">
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">{t('maintenance.statActive')}</p>
              <p className="text-lg sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{stats.total}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-primary/20 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <FileCheck className="w-4 h-4 sm:w-6 sm:h-6 text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 min-h-[80px] sm:min-h-[100px]">
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">{t('maintenance.stat1Month')}</p>
              <p className="text-lg sm:text-2xl font-semibold text-red-600">{stats.expiring1Month}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-red-100 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <AlertCircle className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 min-h-[80px] sm:min-h-[100px]">
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">{t('maintenance.stat3Month')}</p>
              <p className="text-lg sm:text-2xl font-semibold text-orange-600">{stats.expiring3Months}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 min-h-[80px] sm:min-h-[100px]">
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">{t('maintenance.stat6Month')}</p>
              <p className="text-lg sm:text-2xl font-semibold text-yellow-600">{stats.expiring6Months}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-yellow-100 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <Calendar className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filtreler */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'en' ? 'Search' : 'Arama'}
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('maintenance.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'en' ? 'Status' : 'Durum'}
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="">{t('maintenance.allStatuses')}</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'en' ? 'Supplier' : 'Tedarikçi'}
            </label>
            <select
              value={selectedSupplier}
              onChange={(e) => setSelectedSupplier(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="">{t('maintenance.allSuppliers')}</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedStatus('');
                setSelectedSupplier('');
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
            >
              {language === 'en' ? 'Clear' : 'Temizle'}
            </button>
          </div>
        </div>
      </div>

      {/* Anlaşmalar Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Contract Name' : 'Anlaşma Adı'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Supplier' : 'Tedarikçi'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Start Date' : 'Başlangıç Tarihi'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'End Date / Days Left' : 'Bitiş Tarihi / Kalan Gün'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Price' : 'Fiyat'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Status' : 'Durum'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Contract' : 'Sözleşme'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Actions' : 'İşlemler'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {contracts.map((contract) => (
                <tr key={contract.id} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {contract.contract_name}
                    </div>
                    {contract.description && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {contract.description}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {contract.supplier_name || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {new Date(contract.contract_start_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {new Date(contract.contract_end_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                    </div>
                    {(() => {
                      const daysRemaining = getDaysRemaining(contract.contract_end_date);
                      if (daysRemaining < 0) {
                        return (
                          <div className="text-xs text-red-600 dark:text-red-400 font-medium">
                            {language === 'en' ? `Expired (${Math.abs(daysRemaining)} days ago)` : `Süresi dolmuş (${Math.abs(daysRemaining)} gün önce)`}
                          </div>
                        );
                      } else if (daysRemaining <= 30) {
                        return (
                          <div className="text-xs text-red-600 dark:text-red-400 font-medium">
                            {language === 'en' ? `${daysRemaining} days left (⚠️ Urgent)` : `${daysRemaining} gün kaldı (⚠️ Acil)`}
                          </div>
                        );
                      } else if (daysRemaining <= 90) {
                        return (
                          <div className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                            {language === 'en' ? `${daysRemaining} days left` : `${daysRemaining} gün kaldı`}
                          </div>
                        );
                      } else if (daysRemaining <= 180) {
                        return (
                          <div className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                            {language === 'en' ? `${daysRemaining} days left` : `${daysRemaining} gün kaldı`}
                          </div>
                        );
                      } else {
                        return (
                          <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                            {language === 'en' ? `${daysRemaining} days left` : `${daysRemaining} gün kaldı`}
                          </div>
                        );
                      }
                    })()
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {contract.contract_price ?
                        `${contract.contract_price.toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')} ${currencyLabels[contract.contract_currency || 'TL']}`
                        : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[contract.status]}`}>
                      {getStatusIcon(contract.status)}
                      <span className="ml-1">{statusLabels[contract.status]}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {contract.contract_file_path ? (
                      <button
                        onClick={() => downloadContract(contract)}
                        className="flex items-center text-primary hover:text-primary-dark dark:text-primary dark:hover:text-accent"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        <span className="text-sm">{t('maintenance.download')}</span>
                      </button>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400">{language === 'en' ? 'None' : 'Yok'}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setSelectedContract(contract);
                          fetchContractHistory(contract.id);
                          setShowHistoryModal(true);
                        }}
                        className="text-gray-600 hover:text-gray-800 dark:text-gray-200 dark:text-gray-400 dark:hover:text-gray-300"
                        title={language === 'en' ? 'History' : 'Geçmiş'}
                      >
                        <History className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedContract(contract);
                          setShowExtendModal(true);
                        }}
                        className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                        title={language === 'en' ? 'Extend Period' : 'Süre Uzat'}
                      >
                        <CalendarPlus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedContract(contract);
                          fetchContractFiles(contract.id);
                          setShowContractsModal(true);
                        }}
                        className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300"
                        title={language === 'en' ? 'Contracts' : 'Sözleşmeler'}
                      >
                        <Paperclip className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(contract)}
                        className="text-primary hover:text-primary-dark dark:text-primary dark:hover:text-accent"
                        title={language === 'en' ? 'Edit' : 'Düzenle'}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedContract(contract);
                          setShowDeleteModal(true);
                        }}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        title={language === 'en' ? 'Delete' : 'Sil'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                {language === 'en' ? 'Page' : 'Sayfa'} {currentPage} / {totalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  {language === 'en' ? 'Previous' : 'Önceki'}
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  {language === 'en' ? 'Next' : 'Sonraki'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Yeni/Düzenle Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {selectedContract ? t('maintenance.editTitle') : t('maintenance.newTitle')}
              </h3>
            </div>
            
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {language === 'en' ? 'Contract Name *' : 'Anlaşma Adı *'}
                </label>
                <input
                  type="text"
                  required
                  value={formData.contract_name}
                  onChange={(e) => setFormData({ ...formData, contract_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {language === 'en' ? 'Supplier' : 'Tedarikçi'}
                </label>
                <select
                  value={formData.supplier_id}
                  onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="">{t('maintenance.selectSupplier')}</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Start Date *' : 'Başlangıç Tarihi *'}
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.contract_start_date}
                    onChange={(e) => setFormData({ ...formData, contract_start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'End Date *' : 'Bitiş Tarihi *'}
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.contract_end_date}
                    onChange={(e) => setFormData({ ...formData, contract_end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {language === 'en' ? 'Status' : 'Durum'}
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  {Object.entries(statusLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Contract Price' : 'Anlaşma Fiyatı'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.contract_price}
                    onChange={(e) => setFormData({ ...formData, contract_price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Currency' : 'Para Birimi'}
                  </label>
                  <select
                    value={formData.contract_currency}
                    onChange={(e) => setFormData({ ...formData, contract_currency: e.target.value as 'TL' | 'EUR' | 'USD' })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="TL">{t('maintenance.currencyTL')}</option>
                    <option value="EUR">Euro (€)</option>
                    <option value="USD">Dolar ($)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {language === 'en' ? 'Description' : 'Açıklama'}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              {!selectedContract && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Contract File' : 'Sözleşme Dosyası'}
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setContractFile(e.target.files?.[0] || null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {language === 'en' ? 'PDF, DOC, DOCX files accepted (Max: 10MB)' : 'PDF, DOC, DOCX dosyaları kabul edilir (Max: 10MB)'}
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('maintenance.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={uploadingContract}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {uploadingContract ? t('maintenance.uploading') : (selectedContract ? t('maintenance.update') : t('maintenance.save'))}
                </button>
              </div>
            </form>
          </div>
        </Modal>

      {/* Silme Onay Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
          {selectedContract && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[85vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {language === 'en' ? 'Delete Contract' : 'Bakım Anlaşmasını Sil'}
              </h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {language === 'en'
                ? `Are you sure you want to delete "${selectedContract.contract_name}"? This action cannot be undone.`
                : `"${selectedContract.contract_name}" bakım anlaşmasını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
              >
                {t('maintenance.cancel')}
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {language === 'en' ? 'Delete' : 'Sil'}
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* Süre Uzatma Modal */}
      <Modal isOpen={showExtendModal} onClose={() => setShowExtendModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[85vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {language === 'en' ? 'Extend Contract Period' : 'Anlaşma Süresini Uzat'}
              </h3>
            </div>
            <form onSubmit={handleExtendContract} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {language === 'en' ? 'Extension Type' : 'Uzatma Türü'}
                </label>
                <select
                  value={extendData.extendType}
                  onChange={(e) => setExtendData({ ...extendData, extendType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="days">{t('maintenance.extendDays')}</option>
                  <option value="months">{t('maintenance.extendMonths')}</option>
                  <option value="years">{t('maintenance.extendYears')}</option>
                  <option value="date">{language === 'en' ? 'Specific Date' : 'Belirli Tarih'}</option>
                </select>
              </div>

              {extendData.extendType !== 'date' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Extension Amount' : 'Uzatma Miktarı'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={extendData.extendValue}
                    onChange={(e) => setExtendData({ ...extendData, extendValue: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder={language === 'en'
                      ? `How many ${extendData.extendType === 'days' ? 'days' : extendData.extendType === 'months' ? 'months' : 'years'}?`
                      : `Kaç ${extendData.extendType === 'days' ? 'gün' : extendData.extendType === 'months' ? 'ay' : 'yıl'}?`}
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'New End Date' : 'Yeni Bitiş Tarihi'}
                  </label>
                  <input
                    type="date"
                    required
                    value={extendData.newEndDate}
                    onChange={(e) => setExtendData({ ...extendData, newEndDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {language === 'en' ? 'Notes' : 'Notlar'}
                </label>
                <textarea
                  value={extendData.notes}
                  onChange={(e) => setExtendData({ ...extendData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder={language === 'en' ? 'Reason for extension...' : 'Süre uzatma sebebi...'}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowExtendModal(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('maintenance.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  {language === 'en' ? 'Extend Period' : 'Süreyi Uzat'}
                </button>
              </div>
            </form>
          </div>
        </Modal>

      {/* Geçmiş Modal */}
      <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)}>
          {selectedContract && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {selectedContract.contract_name} - {language === 'en' ? 'History' : 'Geçmiş'}
              </h3>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-4">
                {contractHistory.length > 0 ? (
                  contractHistory.map((history: any, index) => (
                    <div key={index} className="border-l-4 border-primary pl-4 py-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {history.action_type}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {history.notes}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 dark:text-gray-400">
                            {history.created_by_name} - {new Date(history.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    {language === 'en' ? 'No history records yet.' : 'Henüz geçmiş kaydı bulunmuyor.'}
                  </p>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
              >
                {language === 'en' ? 'Close' : 'Kapat'}
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* Sözleşmeler Modal */}
      <Modal isOpen={showContractsModal} onClose={() => setShowContractsModal(false)}>
          {selectedContract && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {selectedContract.contract_name} - {language === 'en' ? 'Contract Files' : 'Sözleşme Dosyaları'}
              </h3>
            </div>
            <div className="px-6 py-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {language === 'en' ? 'Upload New Contract' : 'Yeni Sözleşme Yükle'}
                </label>
                <div className="flex space-x-3">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    id="contract-file-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file && file.size > 50 * 1024 * 1024) {
                        alert(language === 'en' ? 'File size cannot exceed 50MB!' : 'Dosya boyutu 50MB\'dan büyük olamaz!');
                        e.target.value = '';
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                  <button
                    onClick={() => {
                      const fileInput = document.getElementById('contract-file-input') as HTMLInputElement;
                      const file = fileInput?.files?.[0];
                      if (file && selectedContract) {
                        uploadNewContract(file, selectedContract.id);
                        fileInput.value = '';
                      } else {
                        alert(language === 'en' ? 'Please select a file first!' : 'Lütfen önce bir dosya seçin!');
                      }
                    }}
                    disabled={isUploading}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isUploading ? t('maintenance.uploading') : t('maintenance.upload')}
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {language === 'en' ? 'PDF, DOC, DOCX files accepted (Maximum: 50MB)' : 'PDF, DOC, DOCX dosyaları kabul edilir (Maksimum: 50MB)'}
                </p>
                
                {isUploading && (
                  <div className="mt-3">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                      <span>{t('maintenance.uploading')}</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900 dark:text-gray-100">{language === 'en' ? 'Existing Files:' : 'Mevcut Dosyalar:'}</h4>
                {contractFiles.length > 0 ? (
                  contractFiles.map((file: any, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg">
                      <div className="flex items-center">
                        <FileText className="w-5 h-5 text-primary mr-3" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {file.filename}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {file.uploaded_by_name} - {new Date(file.uploaded_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          const response = await fetch(`/api/maintenance-contracts/files/${file.id}/download`, {
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
                          });
                          if (!response.ok) return;
                          const url = window.URL.createObjectURL(await response.blob());
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = file.filename;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                        }}
                        className="text-primary hover:text-primary-dark dark:text-primary dark:hover:text-accent mr-2"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(t('maintenance.confirmDeleteFile'))) {
                            deleteContractFile(file.id, selectedContract!.id);
                          }
                        }}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                    {language === 'en' ? 'No files uploaded yet.' : 'Henüz dosya yüklenmemiş.'}
                  </p>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowContractsModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
              >
                {language === 'en' ? 'Close' : 'Kapat'}
              </button>
            </div>
          </div>
          )}
        </Modal>
    </div>
  );
};

export default MaintenanceContractsPage;
